import { useState, useRef, useEffect } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { Offcanvas, OffcanvasBody } from 'reactstrap';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Sidebar, { SidebarNav } from './sidebar';
import Header from './header';
import OrgSwitcher from '../common/OrgSwitcher';
import TrialBanner from '../billing/trialBanner';

// Captures the outlet element on mount so it stays frozen during
// AnimatePresence exit — without this, useOutlet() returns the NEW
// route's element the moment the location changes.
function FrozenOutlet() {
    const outlet = useOutlet();
    const [frozen] = useState(outlet);
    return frozen;
}

const pageVariants = {
    initial: { x: 40, opacity: 0 },
    animate: {
        x: 0, opacity: 1,
        transition: {
            duration: 0.2,
            ease: [0.25, 0.1, 0.25, 1],
            // Tiny opacity delay lets heavy pages (e.g. dashboard grid)
            // finish their layout calculations while still invisible.
            opacity: { delay: 0.06 },
        },
    },
    exit: {
        opacity: 0,
        transition: { duration: 0.1, ease: 'easeOut' },
    },
};

// Responsive shell:
//   ≥md — static <Sidebar /> flush-left, header + content to its right
//   <md — static sidebar hidden via CSS (see styles.scss §5); header shows a
//         hamburger that toggles the <Offcanvas> drawer below, which renders
//         the same <SidebarNav /> plus an OrganizationSwitcher at the bottom
//         (the switcher is hidden from the header on mobile too, so the
//         drawer is the only place multi-org users can switch).
// The drawer is always mounted; the <md media query is what makes the
// hamburger button visible so it only ever opens there.
export default function AppLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);
  const location = useLocation();
  const contentRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Reset scroll when navigating to a new page. With mode="wait",
  // this fires after the exit completes and the new page mounts.
  useEffect(() => {
      if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.pathname]);

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <Header onMenuClick={() => setNavOpen(true)} />
        <TrialBanner />
        <main className="content" ref={contentRef}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={prefersReducedMotion ? undefined : pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={prefersReducedMotion ? { duration: 0 } : undefined}
            >
              <FrozenOutlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <Offcanvas
        isOpen={navOpen}
        toggle={() => setNavOpen(v => !v)}
        direction="start"
        className="sidebar-drawer"
      >
        <OffcanvasBody className="p-0 d-flex flex-column">
          <div className="flex-grow-1">
            <SidebarNav onNavigate={closeNav} />
          </div>
          <div className="p-3 border-top">
            <OrgSwitcher />
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
}
